import { createAsyncThunk } from "@reduxjs/toolkit";
import { USER } from "constants/api";
import axios from "utils/http";

export interface IUsersResponse extends ApiResponse {
  data: IUsers;
}

export interface IFetchUsersArgs extends ReduxAction {
  page: number;
  curr?: any;
  queries: IUserSearchParams;
}

export interface IFetchSingleUserResponse extends ApiResponse {
  data: IUserDetails;
}

export interface IFetchSingleUserArgs extends ReduxAction {
  id: number;
}

export interface IRestrictUserArgs extends ReduxAction {
  payload: IRestrictUser;
  userId: number;
}

export interface IUpdateUserDetailsArgs extends ReduxAction {
  payload: IUpdateUserDetails;
  userId: number;
}

export interface IUpdateUserResponse extends ApiResponse {
  data: IUserDetails;
}

export interface IApproveOrRejectUserArgs extends ReduxAction {
  payload: IApproveOrRejectUser;
  userId: number;
}

export interface IDeleteUserArgs extends ReduxAction {
  payload: IDeleteUser;
  userId: number;
}

export const fetchUsers = createAsyncThunk(
  USER.LIST,
  async (
    { page, queries, onFailure }: IFetchUsersArgs,
    { rejectWithValue }
  ) => {
    try {
      const searchParams = new URLSearchParams();

      if (Object.values(queries)?.length) {
        Object.entries(queries).forEach(([key, value]) => {
          if (value) {
            searchParams.append(`${key}`, value);
          }
        });
      }

      const { data }: IUsersResponse = await axios.get(
        `${USER.LIST}?page=${page}`,
        {
          params: searchParams,
        }
      );

      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const fetchPendingVirtualAccountUsers = createAsyncThunk(
  USER.PENDING_VA_REQUESTS,
  async (
    { page, curr, queries, onFailure }: IFetchUsersArgs,
    { rejectWithValue }
  ) => {
    try {
      const searchParams = new URLSearchParams();

      if (Object.values(queries)?.length) {
        Object.entries(queries).forEach(([key, value]) => {
          if (value) {
            searchParams.append(`${key}`, value);
          }
        });
      }

      const { data }: IUsersResponse = await axios.get(
        `${USER.PENDING_VA_REQUESTS.replace(":curr", String(curr))}?page=${page}`,
        {
          params: searchParams,
        }
      );

      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const fetchSingleUser = createAsyncThunk(
  USER.DETAIL,
  async ({ id, onFailure }: IFetchSingleUserArgs, { rejectWithValue }) => {
    try {
      const { data }: IFetchSingleUserResponse = await axios.get(
        USER.DETAIL.replace(":id", String(id))
      );

      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const restrictUser = createAsyncThunk(
  USER.RESTRICT,
  async (
    { payload, userId, onSuccess, onFailure }: IRestrictUserArgs,
    { rejectWithValue }
  ) => {
    try {
      const { message }: ApiResponse = await axios.post(
        USER.RESTRICT.replace(":userId", String(userId)),
        payload
      );
      if (onSuccess) onSuccess(message);
      return message;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const approveOrRejectUser = createAsyncThunk(
  USER.MANUAL_REVIEW,
  async (
    { payload, userId, onSuccess, onFailure }: IApproveOrRejectUserArgs,
    { rejectWithValue }
  ) => {
    try {
      const { message }: ApiResponse = await axios.post(
        USER.MANUAL_REVIEW.replace(":userId", String(userId)),
        payload
      );
      if (onSuccess) onSuccess(message);
      return message;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const updateBvnStatus = createAsyncThunk(
  USER.UPDATE_BVN_STATUS,
  async (
    { payload, onSuccess, onFailure }: any,
    { rejectWithValue }
  ) => {
    try {
      const { message }: ApiResponse = await axios.post(
        USER.UPDATE_BVN_STATUS,
        payload
      );
      if (onSuccess) onSuccess(message);
      return message;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const updateIdentityDocStatus = createAsyncThunk(
  USER.UPDATE_DOCUMENT_VERIFICATION,
  async (
    { payload, onSuccess, onFailure }: any,
    { rejectWithValue }
  ) => {
    try {
      const { message }: ApiResponse = await axios.post(
        USER.UPDATE_DOCUMENT_VERIFICATION,
        payload
      );
      if (onSuccess) onSuccess(message);
      return message;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const updateAddressVerificationStatus = createAsyncThunk(
  USER.UPDATE_ADDRESS_VERIFICATION,
  async (
    { payload, user_id, onSuccess, onFailure }: any,
    { rejectWithValue }
  ) => {
    try {
      const { message }: ApiResponse = await axios.post(
        USER.UPDATE_ADDRESS_VERIFICATION.replace(":userId", String(user_id)),
        payload
      );
      if (onSuccess) onSuccess(message);
      return message;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const updatePassport = createAsyncThunk(
  USER.UPDATE_PASSPORT,
  async (
    { payload, user_id, onSuccess, onFailure }: any,
    { rejectWithValue }
  ) => {
    try {
      const { message }: ApiResponse = await axios.post(
        USER.UPDATE_PASSPORT.replace(":userId", String(user_id)),
        payload
      );
      if (onSuccess) onSuccess(message);
      return message;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const updateAccountRequest = createAsyncThunk(
  USER.UPDATE_ACCOUNT_REQUEST,
  async (
    { payload, user_id, request_id, onSuccess, onFailure }: any,
    { rejectWithValue }
  ) => {
    try {
      const { message }: ApiResponse = await axios.post(
        USER.UPDATE_ACCOUNT_REQUEST.replace(
          ":userId",
          String(user_id)
        ).replace(":requestId", String(request_id)),
        payload
      );
      if (onSuccess) onSuccess(message);
      return message;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const updateUserDetails = createAsyncThunk(
  `${USER.DETAIL}/update`,
  async (
    { payload, userId, onSuccess, onFailure }: IUpdateUserDetailsArgs,
    { rejectWithValue }
  ) => {
    try {
      const { data }: IUpdateUserResponse = await axios.put(
        USER.DETAIL.replace(":id", String(userId)),
        payload
      );
      if (onSuccess) onSuccess(data);
      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const deleteUserAccount = createAsyncThunk(
  `${USER.DETAIL}/delete`,
  async (
    { payload, userId, onSuccess, onFailure }: IDeleteUserArgs,
    { rejectWithValue }
  ) => {
    try {
      const { message }: ApiResponse = await axios.delete(
        USER.DETAIL.replace(":id", String(userId)),
        {
          data: payload,
        }
      );
      if (onSuccess) onSuccess(message);
      return message;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);
